import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and store slug
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const storeSlug = localStorage.getItem('storeSlug');

  config.headers = config.headers || {};

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach store slug header unless explicitly skipped per request
  if (storeSlug && !config.skipStoreSlug) {
    config.headers['X-Store-Slug'] = storeSlug;
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', null, { params: { token, new_password: newPassword } }),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Store API
export const storeAPI = {
  list: () => api.get('/stores'),
  getBySlug: (slug, config = {}) => api.get(`/stores/${slug}`, config),

  // Public store config: force X-Store-Slug from caller, and prevent interceptor from injecting a different slug
  getConfig: (slug, config = {}) => {
    const headers = { ...(config.headers || {}), 'X-Store-Slug': slug };
    return api.get('/public/store-config', { ...config, headers, skipStoreSlug: true });
  },

  // Public booking helpers
  publicServices: (slug, config = {}) => {
    const headers = { ...(config.headers || {}), 'X-Store-Slug': slug };
    return api.get('/public/services', { ...config, headers, skipStoreSlug: true });
  },
  publicStaff: (slug, config = {}) => {
    const headers = { ...(config.headers || {}), 'X-Store-Slug': slug };
    return api.get('/public/staff', { ...config, headers, skipStoreSlug: true });
  },
  publicCategories: (slug, config = {}) => {
    const headers = { ...(config.headers || {}), 'X-Store-Slug': slug };
    return api.get('/public/categories', { ...config, headers, skipStoreSlug: true });
  },

  create: (data) => api.post('/stores', data),
  update: (storeId, data) => api.put(`/stores/${storeId}`, data),
  updateModules: (storeId, modules) => api.post(`/stores/${storeId}/modules`, modules),
  delete: (storeId) => api.delete(`/stores/${storeId}`),
  createStoreAdmin: (storeId, data) => api.post(`/stores/${storeId}/admin`, data),
  getHours: () => api.get('/store/hours'),
  updateHours: (hours) => api.put('/store/hours', { hours }),
  getClosures: () => api.get('/store/closures'),
  createClosure: (date, reason) => api.post('/store/closures', null, { params: { date, reason } }),
  deleteClosure: (closureId) => api.delete(`/store/closures/${closureId}`),
};

// Staff API
export const staffAPI = {
  list: () => api.get('/staff'),
  create: (data) => api.post('/staff', data),
  update: (staffId, data) => api.put(`/staff/${staffId}`, data),
  delete: (staffId) => api.delete(`/staff/${staffId}`),
  getShifts: (staffId) => api.get(`/staff/${staffId}/shifts`),
  createShift: (data) => api.post('/staff/shifts', data),
  getAbsences: (staffId) => api.get(`/staff/${staffId}/absences`),
  createAbsence: (data) => api.post('/staff/absences', data),
  recordPresence: (type, notes) => api.post('/staff/presence', null, { params: { type, notes } }),
  getPresence: (staffId, date) => api.get('/staff/presence', { params: { staff_id: staffId, date } }),
  getPresenceSummary: (staffId, startDate, endDate) =>
    api.get('/staff/presence/summary', {
      params: { staff_id: staffId, start_date: startDate, end_date: endDate },
    }),
  getServices: (staffId) => api.get(`/staff/${staffId}/services`),
  updateServices: (staffId, data) => api.put(`/staff/${staffId}/services`, data),
};

// Shift API
export const shiftAPI = {
  list: (staffId) => api.get(`/staff/${staffId}/shifts`),
  create: (data) => api.post('/staff/shifts', data),
  update: (shiftId, data) => api.put(`/staff/shifts/${shiftId}`, data),
  delete: (shiftId) => api.delete(`/staff/shifts/${shiftId}`),
};

// Category API
export const categoryAPI = {
  list: (type) => api.get('/categories', { params: { type } }),
  create: (data) => api.post('/categories', data),
  update: (categoryId, data) => api.put(`/categories/${categoryId}`, data),
  delete: (categoryId) => api.delete(`/categories/${categoryId}`),
};

// Service API
export const serviceAPI = {
  list: () => api.get('/services'),
  create: (data) => api.post('/services', data),
  update: (serviceId, data) => api.put(`/services/${serviceId}`, data),
  delete: (serviceId) => api.delete(`/services/${serviceId}`),
  getAvailableStaff: (serviceId) => api.get(`/services/${serviceId}/available-staff`),
};

// Appointment API
export const appointmentAPI = {
  getSlots: (staffId, date, serviceId) =>
    api.get('/appointments/slots', { params: { staff_id: staffId, date, service_id: serviceId } }),
  create: (data) => api.post('/appointments', data),
  list: (params) => api.get('/appointments', { params }),
  get: (appointmentId) => api.get(`/appointments/${appointmentId}`),
  update: (appointmentId, data) => api.put(`/appointments/${appointmentId}`, data),
  updateStatus: (appointmentId, status) =>
    api.put(`/appointments/${appointmentId}/status`, null, { params: { status } }),
  delete: (appointmentId) => api.delete(`/appointments/${appointmentId}`),
  cancel: (appointmentId) => api.delete(`/appointments/${appointmentId}`),
};

// Product API
export const productAPI = {
  list: () => api.get('/products'),
  create: (data) => api.post('/products', data),
  update: (productId, data) => api.put(`/products/${productId}`, data),
  delete: (productId) => api.delete(`/products/${productId}`),
};

// Order API
export const orderAPI = {
  create: (data) => api.post('/orders', data),
  list: () => api.get('/orders'),
  get: (orderId) => api.get(`/orders/${orderId}`),
  updateStatus: (orderId, status) => api.put(`/orders/${orderId}/status`, null, { params: { status } }),
  cancel: (orderId) => api.delete(`/orders/${orderId}`),
};

// Payment API
export const paymentAPI = {
  createStripeSession: (orderId, originUrl) =>
    api.post('/payments/stripe/create-session', null, { params: { order_id: orderId, origin_url: originUrl } }),
  getStripeStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`),
  createPayPalPayment: (orderId, returnUrl, cancelUrl) =>
    api.post('/payments/paypal/create', null, {
      params: { order_id: orderId, return_url: returnUrl, cancel_url: cancelUrl },
    }),
  capturePayPal: (paymentId) => api.post(`/payments/paypal/capture/${paymentId}`),
};

// Review API
export const reviewAPI = {
  create: (data) => api.post('/reviews', data),
  list: (staffId) => api.get('/reviews', { params: { staff_id: staffId } }),
  getStats: () => api.get('/reviews/stats'),
};

// Reports API
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getAppointmentsTrend: (days = 30) => api.get('/reports/appointments-trend', { params: { days } }),
  getRevenueTrend: (days = 30) => api.get('/reports/revenue-trend', { params: { days } }),
  exportPDF: (reportType = 'dashboard', startDate, endDate) =>
    api.get('/reports/pdf', {
      params: { report_type: reportType, start_date: startDate, end_date: endDate },
      responseType: 'blob',
    }),
};

// Discount Codes API
export const discountAPI = {
  list: () => api.get('/admin/discount-codes'),
  create: (data) => api.post('/admin/discount-codes', data),
  validate: (code, orderTotal, orderType = 'all') =>
    api.post('/discount-codes/validate', null, {
      params: { code, order_total: orderTotal, order_type: orderType },
    }),
  apply: (code) => api.post('/discount-codes/apply', null, { params: { code } }),
  delete: (codeId) => api.delete(`/admin/discount-codes/${codeId}`),
};

// Staff Presence API (Timbrature)
export const presenceAPI = {
  clockIn: (note = null) => api.post('/staff/clock-in', { note }),
  clockOut: (note = null) => api.post('/staff/clock-out', { note }),
  getToday: () => api.get('/staff/presence/today'),
  getHistory: (params) => api.get('/staff/presence/history', { params }),
  getSummary: (month) => api.get('/staff/presence/summary', { params: { month } }),
};

// Waitlist API
export const waitlistAPI = {
  add: (data) => api.post('/waitlist', data),
  list: (status) => api.get('/waitlist', { params: { status } }),
  updateStatus: (entryId, status) => api.put(`/waitlist/${entryId}/status`, null, { params: { status } }),
  delete: (entryId) => api.delete(`/waitlist/${entryId}`),
};

// WhatsApp API
export const whatsappAPI = {
  getStatus: () => api.get('/admin/whatsapp/status'),
  send: (toPhone, message) =>
    api.post('/admin/whatsapp/send', null, { params: { to_phone: toPhone, message } }),
  notifyAppointment: (appointmentId) =>
    api.post('/admin/whatsapp/notify-appointment', null, { params: { appointment_id: appointmentId } }),
};

// Admin API
export const adminAPI = {
  listStores: () => api.get('/admin/stores'),
  getStats: () => api.get('/admin/stats'),
  impersonate: (userId) => api.post(`/admin/impersonate/${userId}`),
  getAuditLogs: (params) => api.get('/audit-logs', { params }),
};

// Image Upload API
export const imageAPI = {
  upload: (imageData, entityType, entityId = null) =>
    api.post('/upload/image', { image_data: imageData, entity_type: entityType, entity_id: entityId }),
  delete: (imageId) => api.delete(`/images/${imageId}`),
};

// CSV Import/Export API
export const csvAPI = {
  // Export functions (returns blob for download)
  exportCategories: () => api.get('/admin/export/categories', { responseType: 'blob' }),
  exportCategoriesTemplate: () => api.get('/admin/export/categories/template', { responseType: 'blob' }),
  exportServices: () => api.get('/admin/export/services', { responseType: 'blob' }),
  exportServicesTemplate: () => api.get('/admin/export/services/template', { responseType: 'blob' }),
  exportProducts: () => api.get('/admin/export/products', { responseType: 'blob' }),
  exportProductsTemplate: () => api.get('/admin/export/products/template', { responseType: 'blob' }),
  exportClients: () => api.get('/admin/export/clients', { responseType: 'blob' }),
  exportClientsTemplate: () => api.get('/admin/export/clients/template', { responseType: 'blob' }),
  exportStaff: () => api.get('/admin/export/staff', { responseType: 'blob' }),
  exportStaffTemplate: () => api.get('/admin/export/staff/template', { responseType: 'blob' }),
  exportAppointments: (dateFrom, dateTo) =>
    api.get('/admin/export/appointments', {
      params: { date_from: dateFrom, date_to: dateTo },
      responseType: 'blob',
    }),
  exportAppointmentsTemplate: () => api.get('/admin/export/appointments/template', { responseType: 'blob' }),
  exportStoreHours: () => api.get('/admin/export/store-hours', { responseType: 'blob' }),
  exportStoreHoursTemplate: () => api.get('/admin/export/store-hours/template', { responseType: 'blob' }),
  exportClosures: () => api.get('/admin/export/closures', { responseType: 'blob' }),
  exportClosuresTemplate: () => api.get('/admin/export/closures/template', { responseType: 'blob' }),

  // Import functions (accepts FormData with file)
  importCategories: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/categories', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importServices: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/services', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importClients: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/clients', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importStaff: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/staff', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importAppointments: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/appointments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importStoreHours: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/store-hours', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importClosures: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/closures', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Loyalty/Fidelity API
export const loyaltyAPI = {
  getConfig: () => api.get('/loyalty/config'),
  updateConfig: (data) => api.put('/loyalty/config', data),
  getRewards: () => api.get('/loyalty/rewards'),
  createReward: (data) => api.post('/loyalty/rewards', data),
  updateReward: (id, data) => api.put(`/loyalty/rewards/${id}`, data),
  deleteReward: (id) => api.delete(`/loyalty/rewards/${id}`),
  getTopCustomers: (limit = 10) => api.get(`/loyalty/top-customers?limit=${limit}`),
  getStats: () => api.get('/loyalty/stats'),
  getUserPoints: (userId) => api.get(`/loyalty/users/${userId}/points`),
  addPoints: (userId, points, reason) => api.post(`/loyalty/users/${userId}/points`, { points, reason }),
  redeemReward: (userId, rewardId) => api.post(`/loyalty/users/${userId}/redeem`, { reward_id: rewardId }),
};

// Notification API
export const notificationAPI = {
  getConfig: () => api.get('/notifications/config'),
  testEmail: (to) => api.post(`/notifications/test/email?to=${to}`),
  testSMS: (to) => api.post(`/notifications/test/sms?to=${to}`),
  testWhatsApp: (to) => api.post(`/notifications/test/whatsapp?to=${to}`),
};

// Helper function to download CSV blob
export const downloadCSV = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export default api;
